import { LoadingState } from 'src/app/utilities/state.util';
import { GetBlueSageTerminalsQuery } from 'src/generated/graphql.generated';

/** One device as the roster reports it. */
export type BlueSageTerminal = GetBlueSageTerminalsQuery['blueSageTerminals'][number];

/**
 * The terminal picker — the shared surface every money-moving terminal action
 * sits on top of.
 *
 * ## EPI only. No labels.
 *
 * Decided 2026-08-24 and it is not an aesthetic choice: a label is a property
 * of the DEVICE, devices move between trucks, and EPIs persist and can be moved
 * to a replacement unit. A stale label on a money screen is worse than no
 * label, because it reads as authoritative. The backend still returns one; this
 * feature does not ask for it.
 *
 * ## Status is three-valued, and none of the three disables Send
 *
 * `online` · `offline` · **`unknown`**. `unknown` means Valor's device-status
 * service could not be reached and the roster we are showing is cached — it is
 * **not** a statement that the terminal is down.
 *
 * The spec is explicit and it matches what we had already decided:
 * *"Do not disable Send on a reading from this endpoint."* The roster read is
 * live when the picker opens, and then the operator reads the amount, checks
 * the job, talks to the customer, and presses Send a minute later. The dot on
 * screen is a minute old; the middleware's check at push time is not. Offline
 * and unknown rows are therefore shown, dimmed, and **selectable**, and the
 * failure message afterwards is accurate rather than a guess.
 *
 * ## Busy is different, and it DOES disable Send
 *
 * `busy` is not a status reading — it is this CRM's own device lock, and it
 * means a push we started is live on that reader right now. Sending a second
 * amount would put two jobs' money on one device in front of a customer. That
 * is the one case where the picker refuses.
 */
export const blueSageTerminalPickerInitialState = {
    terminals: [] as BlueSageTerminal[],

    loading: LoadingState.INIT as LoadingState,
    /**
     * True when the last load FAILED, as distinct from succeeding with nothing.
     *
     * The same trap the review queue fell into: `LoadingState` has no ERROR
     * member, so a failed load settles on LOADED and an empty list renders as
     * "no terminals" — which on a payment screen reads as "this location has no
     * devices" rather than "we could not ask".
     */
    loadFailed: false as boolean,

    /** Substring typed into the search box. Filtering is client-side; see the effects. */
    search: '' as string,

    /** The EPI the operator has chosen. Selection alone never takes the lock. */
    selectedEpi: undefined as string | undefined,

    /**
     * EPIs this user has recently sent to, most recent first.
     *
     * A convenience, not a record. It lives in `localStorage` keyed per user and
     * zone rather than in Neo4j, so surfacing it costs the vendor no data-model
     * change — and losing it costs nothing but one extra glance.
     */
    recentEpis: [] as string[],

    /**
     * Whether the whole roster is on screen, or just the shortlist.
     *
     * ⚠ A busy location has **thirty** terminals, and this picker sits inside a
     * payment dialog. Rendering all of them pushes the amount, the method and
     * the Send button off the screen on the tablet the payment is being taken
     * on — the list stops being a picker and becomes the page.
     *
     * Collapsed by default, and it costs nothing: the ordering already puts the
     * device this operator last used first, so the shortlist IS the answer in
     * the overwhelming majority of cases.
     */
    expanded: false as boolean,
};

export type BlueSageTerminalPickerState = typeof blueSageTerminalPickerInitialState;

/** How many recents are worth surfacing before the list stops being a shortcut. */
export const RECENT_TERMINALS_MAX = 3;

/**
 * How many terminals the collapsed picker shows.
 *
 * Two, because the ordering is "recently used by you, then online" — so the
 * first two are the device in this operator's hand and the one next to it. A
 * third adds height without adding an answer.
 */
export const TERMINAL_SHORTLIST_SIZE = 2;

/** `localStorage` key, scoped so two users on one machine do not share recents. */
export const recentTerminalsKey = (userId: string, zone: string): string =>
    `bluesage.recentTerminals.${ userId }.${ zone }`;
